import { type SerializeFrom } from '@remix-run/node'
import { useRouteLoaderData } from '@remix-run/react'
import { type loader as rootLoader } from '#app/root.tsx'
import { type PermissionString, parsePermissionString } from './permissions.ts'

// Type alias for clarity
type User = SerializeFrom<typeof rootLoader>['user']

// Type guard for user
function isUser(user: any): user is NonNullable<User> {
	return (
		user &&
		typeof user === 'object' &&
		typeof user.id === 'string' &&
		(user.name === null || typeof user.name === 'string') &&
		Array.isArray(user.platformRoles) &&
		Array.isArray(user.nightclubRoles) &&
		Array.isArray(user.eventRoles)
	)
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

export function userHasPermission(
	user:
		| Pick<NonNullable<User>, 'platformRoles' | 'nightclubRoles' | 'eventRoles'>
		| null
		| undefined,
	permissions: PermissionString | PermissionString[],
) {
	if (!user) return false

	// Normalize permissions to an array
	const permissionList: PermissionString[] = Array.isArray(permissions)
		? permissions
		: permissions.split(',').map((p) => p.trim() as PermissionString)

	// Check each permission
	return permissionList.some((permission) => {
		const { action, entity, access } = parsePermissionString(permission)

		// Check platformRoles
		const hasPlatformPermission = user.platformRoles.some(({ platformRole }) =>
			platformRole.permissions.some(
				(perm) =>
					perm.entity === entity &&
					perm.action === action &&
					(!access || perm.access === access),
			),
		)
		if (hasPlatformPermission) return true

		// Check nightclubRoles
		const hasNightclubPermission = user.nightclubRoles.some(
			({ nightclubRole }) =>
				nightclubRole.permissions.some(
					(perm) =>
						perm.entity === entity &&
						perm.action === action &&
						(!access || perm.access === access),
				),
		)
		if (hasNightclubPermission) return true

		// Check eventRoles
		const hasEventPermission = user.eventRoles.some(({ eventRole }) =>
			eventRole.permissions.some(
				(perm) =>
					perm.entity === entity &&
					perm.action === action &&
					(!access || perm.access === access),
			),
		)

		return hasEventPermission
	})
}

export function userHasRole(
	user:
		| Pick<NonNullable<User>, 'platformRoles' | 'nightclubRoles' | 'eventRoles'>
		| null
		| undefined,
	role: string,
	options: { roleType: 'platform' | 'nightclub' | 'event' } = {
		roleType: 'platform',
	},
) {
	if (!user) return false

	switch (options.roleType) {
		case 'platform':
			return user.platformRoles.some(
				({ platformRole }) => platformRole.name === role,
			)
		case 'nightclub':
			return user.nightclubRoles.some(
				({ nightclubRole }) => nightclubRole.name === role,
			)
		case 'event':
			return user.eventRoles.some(({ eventRole }) => eventRole.name === role)
		default:
			return false
	}
}
